<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BarChart from 'shared/components/charts/BarChart.vue';
import { color } from 'dashboard/components-next/analytics/composables/useThemeColors';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import { useAnalyticsChart } from '../analytics/useAnalyticsChart';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import { QUEM_CORES } from '../atendimento/quemCores';
import { campanhasLabel } from './campanhasLabels';

// A linha do tempo de uma campanha: enviadas, entregues, lidas e respondidas,
// cada uma pela hora em que aconteceu, no fuso da conta. Por hora até 7 dias
// depois do primeiro envio, e por dia depois disso (o servidor decide). Antes
// da captura, entregue e lida valem nulo, e não zero: não se sabe — a barra
// falta, e a tela diz por quê.
const props = defineProps({
  // A resposta de GET campanhas/linha_do_tempo; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
});

const { t, locale } = useI18n();
const { number } = useAnalyticsFormat();
const { palette, bar, chartOptions } = useAnalyticsChart();

// O canvas não lê classe: as cores do tema já resolvidas, com a legenda nas
// classes do mesmo tom. Cor nunca sozinha: toda série tem rótulo na legenda.
const rgb = (name, fallback) => `rgb(${color(name, fallback)})`;
const SERIES = [
  { key: 'sent', legend: 'bg-n-slate-10' },
  { key: 'delivered', legend: 'bg-n-blue-9' },
  { key: 'read', legend: 'bg-n-teal-9' },
  { key: 'replied', legend: QUEM_CORES.disparo.bg },
];
const seriesColor = computed(() => {
  // eslint-disable-next-line no-unused-expressions
  palette.value.tick;
  return {
    sent: palette.value.muted,
    delivered: rgb('--blue-9', '39 129 246'),
    read: rgb('--teal-9', '18 165 148'),
    replied: palette.value.disparo,
  };
});

const points = computed(() => props.report?.points || []);
const byHour = computed(() => props.report?.granularity !== 'day');

const label = at =>
  new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    timeZone: props.report?.timezone,
    day: '2-digit',
    month: '2-digit',
    ...(byHour.value ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(new Date(at));

const collection = computed(() => ({
  labels: points.value.map(point => label(point.at)),
  datasets: SERIES.map(series =>
    bar(
      campanhasLabel(t, 'step', series.key),
      points.value.map(point => point[series.key]),
      seriesColor.value[series.key]
    )
  ),
}));

// O motivo do nulo: a conta sem captura, ou as casas de antes dela.
const reason = computed(() => {
  const withReason = points.value.find(point => point.reason);
  return withReason ? campanhasLabel(t, 'reason', withReason.reason) : '';
});

const hasPoints = computed(() => points.value.length > 0);
const options = computed(() => chartOptions(number));
</script>

<template>
  <AnalyticsPanel
    data-test-id="campanha-linha-do-tempo"
    :title="t('CAMPANHAS.TIMELINE.TITLE')"
    :description="
      report
        ? t(
            byHour ? 'CAMPANHAS.TIMELINE.BY_HOUR' : 'CAMPANHAS.TIMELINE.BY_DAY',
            { timezone: report.timezone }
          )
        : ''
    "
  >
    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>
    <p v-else-if="report && !hasPoints" class="mb-0 text-sm text-n-slate-11">
      {{ t('CAMPANHAS.TIMELINE.EMPTY') }}
    </p>
    <div v-else-if="report" class="flex flex-col gap-2 min-w-0">
      <div class="flex flex-wrap gap-3 items-center text-xs text-n-slate-11">
        <span
          v-for="series in SERIES"
          :key="series.key"
          class="flex gap-1.5 items-center"
        >
          <span class="rounded-sm size-2.5" :class="series.legend" />
          {{ campanhasLabel(t, 'step', series.key) }}
        </span>
      </div>
      <p
        v-if="reason"
        data-test-id="campanha-linha-do-tempo-motivo"
        class="mb-0 text-xs text-n-slate-11"
      >
        {{ t('CAMPANHAS.TIMELINE.NULL_REASON', { reason }) }}
      </p>
      <div class="relative h-56">
        <BarChart :collection="collection" :chart-options="options" />
      </div>
    </div>
  </AnalyticsPanel>
</template>
